import { Component, Emit, Ref, Vue } from 'vue-property-decorator'
import BillingModalInvoicePaymentComponent from '@/modules/billing-module/components/modal/invoice-payment/billing-modal-invoice-payment.vue'
import BillingModalInvoicePayment from '@/modules/billing-module/components/modal/invoice-payment/billing-modal-invoice-payment'
import { InvoicePaymentResponse, InvoicePaymentStatus } from '@/api/services/billing/invoice-payment/billing-invoice-payment.dto'
import { billingInvoicePaymentService } from '@/api/services/billing/invoice-payment/billing-invoice-payment.service'
import { BillingStoreHelper } from '@/modules/billing-module/store/billing-store.helper'
import { StrictNullable } from '@/shared/types/app.types'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { FileUtils } from '@/shared/util/file-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component({
    components: {
        BillingModalInvoicePaymentComponent
    }
})
export default class BillingGridActionsInvoicePayments extends Vue {
    @Ref() readonly invoicePaymentModal!: BillingModalInvoicePayment

    @Emit()
    refresh(): void {
        // emits an event
    }

    private static get _selectedRows(): InvoicePaymentResponse[] {
        return BillingStoreHelper.masterSelectedRows as InvoicePaymentResponse[]
    }
    private static get _selectedRow(): StrictNullable<InvoicePaymentResponse> {
        return BillingGridActionsInvoicePayments._selectedRows.length === 1
            ? BillingGridActionsInvoicePayments._selectedRows[0]
            : null
    }

    get canUpdateInvoicePayment(): boolean {
        return BillingGridActionsInvoicePayments._selectedRows.length === 1 &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICE_PAYMENTS)
    }
    get canCancelInvoicePayment(): boolean {
        return BillingGridActionsInvoicePayments._selectedRow?.status.code === InvoicePaymentStatus.GENERATED &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICE_PAYMENTS)
    }
    get canOpenInvoicePaymentReport(): boolean {
        return BillingGridActionsInvoicePayments._selectedRows.length === 1
    }

    /// SERVICE CALLS ///

    private _downloadInvoicePaymentReport(invoicePaymentId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingInvoicePaymentService.getReport(store.getters.labId, invoicePaymentId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// EVENT HANDLERS ///

    onUpdateInvoicePayment(): void {
        if (BillingGridActionsInvoicePayments._selectedRow) {
            this.invoicePaymentModal.show({
                invoice: BillingGridActionsInvoicePayments._selectedRow.invoice,
                invoicePayment: BillingGridActionsInvoicePayments._selectedRow
            })
        }
    }

    async onCancelInvoicePayment(): Promise<void> {
        if (BillingGridActionsInvoicePayments._selectedRow) {
            const { id, appliedBillableConceptCount } = BillingGridActionsInvoicePayments._selectedRow
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptCount,
                async () => {
                    const response =
                        await PromiseUtils.toCallSecure(
                            billingInvoicePaymentService.cancel(store.state.labId, id))
                    if (response !== null) {
                        ToastUtils.showToastSuccess(this.$tc('accounting.invoice.cancelled'))
                        this.refresh()
                    }
                }
            )
        }
    }

    async onOpenInvoicePaymentReport(): Promise<void> {
        if (BillingGridActionsInvoicePayments._selectedRow) {
            const blob = await this._downloadInvoicePaymentReport(BillingGridActionsInvoicePayments._selectedRow.id)
            if (blob !== null) {
                FileUtils.openFile({ blob })
            }
        }
    }

    ///

    getContextMenuItems(): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (this.canUpdateInvoicePayment) {
            menuItems.push({
                name: this.$tc('accounting.action.updateInvoicePayment'),
                action: this.onUpdateInvoicePayment
            })
        }
        if (this.canCancelInvoicePayment) {
            menuItems.push({
                name: this.$tc('accounting.action.cancelInvoicePayment'),
                action: this.onCancelInvoicePayment
            })
        }
        if (this.canOpenInvoicePaymentReport) {
            menuItems.push({
                name: this.$tc('global.action.openReport'),
                action: this.onOpenInvoicePaymentReport
            })
        }
        return menuItems
    }
}
